import { useSelector, useDispatch } from "react-redux";
import { userSelector } from "../../redux/modules/auth/userSelector";
import { MouseEventHandler, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { updatePassword } from "../../redux/modules/users/actions";
import { RootState } from "../../redux/utils/types";
import NotificationToast from "../../components/NotificationToast";
import { reset } from "../../redux/modules/users/slice";
import Spinner from "../../components/Spinner";
import AdminBar from "../../components/Sidebar/Admin";
import { NavbarToggler } from "../../components/NavbarToggler";
import TextInput from "../../components/TextInput";

export default function AdminSetting() {
  const user = useSelector(userSelector);
  const { status, message } = useSelector((state: RootState) => state.users);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [email, setEmail] = useState(user?.email || "");
  const [passwordVisibility, setPasswordVisibility] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string; confirm?: string }>({});
  const [passWarning, setPassWarning] = useState<boolean>(false);

  const dispatch = useDispatch<any>();

  const passwordValidRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{10,}$/;
  const emailValidRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleUpdateProfile = (e: any) => {
    e.preventDefault();
    const errorMessages: any = {};

    if (!emailValidRegex.test(email)) {
      errorMessages.email = "Invalid email format";
    }

    if (password !== confirmPassword) {
      errorMessages.confirm = "Passwords do not match!";
    }

    if (!passwordValidRegex.test(password)) {
      errorMessages.password =
        "Password must be 10+ characters with at least 1 uppercase, 1 lowercase, 1 number, and 1 special character (@$!%*?&#)";
    }

    setErrors(errorMessages);

    if (Object.keys(errorMessages).length > 0) return;

    dispatch(updatePassword({ id: user.id, password }));
  };

  useEffect(() => {
    if (status === "success") {
      setPassword("");
      setConfirmPassword("");
      setErrors({});
      setTimeout(() => dispatch(reset()), 5000);
    }
  }, [status, dispatch]);

  return (
    <>
      {status === "loading" && <Spinner />}
      <PasswordValidationModal
        show={passWarning}
        onHide={() => setPassWarning(false)}
      />
      <AdminBar />
      <div className="page-content bg-white" id="content">
        <div className="row-header">
          <div className="container-fluid">
            <div className="align-items-center d-flex justify-content-between">
              <h2 className="head">Settings</h2>
              <NavbarToggler />
            </div>
          </div>
        </div>

        <div className="user-box">
          <div className="container-fluid">
            <form onSubmit={handleUpdateProfile}>
              <div className="form-group form-buttons">
                <button type="submit" className="btn suggest-btn pull-right">
                  Save Settings
                </button>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email ID</label>
                <input
                  type="email"
                  className="form-control"
                  id="email"
                  placeholder="Your Email ID"
                  value={email}
                  disabled={true}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {errors.email && <div className="text-danger">{errors.email}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="edit d-flex align-items-center">
                  <input
                    type={passwordVisibility ? "text" : "password"}
                    className="form-control edit-icon"
                    placeholder="Enter new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required={true}
                    pattern={passwordValidRegex.source}
                    title="Must contain at least one number, one uppercase letter, one lowercase letter, one special character (@$!%*?&#) and at least 10 characters"
                    tabIndex={1}
                  />
                  <div className="validation">
                    <button
                      type="button"
                      className="btn"
                      data-toggle="modal"
                      data-target="#validation"
                      onClick={() => setPassWarning(true)}
                    >
                      <i className="fa fa-question-circle-o"></i>
                    </button>
                  </div>
                  <span
                    className="mx-4"
                    title={passwordVisibility ? "Hide Password" : "See Password"}
                    role="button"
                    onClick={() => setPasswordVisibility(!passwordVisibility)}
                  >
                    <i className="fa fa-eye"></i>
                  </span>
                </div>
                {errors.password && <div className="text-danger">{errors.password}</div>}
              </div>

              <TextInput
                label="Confirm Password"
                attrs={{
                  type: "password",
                  id: "c-password",
                  placeholder: "Retype Password",
                  required: true,
                  tabIndex: 2,
                }}
                value={confirmPassword}
                handleChange={(value: string) => {
                  setConfirmPassword(value);
                  if (errors.confirm) {
                    setErrors((prev) => ({ ...prev, confirm: undefined }));
                  }
                }}
                errors={errors.confirm ? [errors.confirm] : []}
              />
            </form>
          </div>
        </div>
      </div>
      <NotificationToast show={status === "success"} message={message} />
    </>
  );
}

function PasswordValidationModal(props: {
  show: boolean;
  onHide: MouseEventHandler;
}) {
  const { show, onHide } = props;

  return (
    <Modal show={show} centered={true} onHide={onHide as any}>
      <div className="modal-content">
        <div className="modal-body">
          <button type="button" className="close" onClick={onHide}>
            <span aria-hidden="true">&times;</span>
          </button>
          <ul>
            <li>10 characters or longer</li>
            <li>At least one number</li>
            <li>At least one lowercase letter</li>
            <li>At least one uppercase letter</li>
            <li>At least one special character (@, $, !, %, *, ?, &, #)</li>
          </ul>
        </div>
      </div>
    </Modal>
  );
}
